import React from 'react';

/**
 * AuthCard — the shell every page of the sign-in / sign-up flow renders into.
 *
 * The flow spans five URLs served by four bundles (login, registration,
 * registration-confirm, recovery) plus post-registration, and each used to
 * describe its own frame. One component instead, so a card that changes
 * changes once: the alternative is five near-identical class strings that
 * drift the moment one of them is touched.
 *
 * Matches the guest-page precedent set by InvitationAccept-V2 — centered
 * card, `default-50` surface, `content1` panel — because a visitor accepting
 * an invitation and a visitor signing up are looking at the same product.
 *
 * @param {object} props
 * @param {string} props.title       Page heading.
 * @param {string} [props.subtitle]  One line under it; omit when the title says enough.
 * @param {'sm'|'lg'} [props.width]  `sm` (default) for the short auth forms,
 *   `lg` for the post-registration profile form, which is two columns wide.
 * @param {React.ReactNode} [props.footer] Links below the card — sign-up,
 *   forgot-password and the like. Outside the panel on purpose: they are
 *   navigation away from this page, not part of its form.
 */
function AuthCard({ title, subtitle, width = 'sm', footer, children }) {
	return (
		<div className="flex w-full justify-center px-4 py-10 sm:py-14">
			<div className={`w-full ${width === 'lg' ? 'max-w-2xl' : 'max-w-md'} flex flex-col gap-5`}>
				<div className="rounded-[14px] border border-default-200 bg-content1 p-6 sm:p-8 shadow-sm flex flex-col gap-6">
					<header className="flex flex-col gap-1">
						{/* The heading text lives on a span: legacy `frontend.css` sets an
						    unlayered `h1 { font-size: 2em }`, which outranks any Tailwind
						    utility on the tag itself (see auth-base.css). */}
						<h1>
							<span className="text-[22px] font-semibold text-foreground">{title}</span>
						</h1>
						{subtitle ? <p className="text-sm text-default-500 m-0">{subtitle}</p> : null}
					</header>

					{children}
				</div>

				{footer ? (
					<div className="text-center text-sm text-default-500 flex flex-col gap-1">{footer}</div>
				) : null}
			</div>
		</div>
	);
}

/**
 * AuthLink — an anchor that survives the layout.
 *
 * `react-heroui-layout.twig` forces `#content a { color: inherit !important }`
 * on every link it serves, and `#content a[class*="text-"]` too, so a Tailwind
 * colour utility cannot win here. `text-primary` is the one exception the
 * layout allows through, which is why every link in this flow uses it.
 */
export function AuthLink({ href, children, ...rest }) {
	return (
		<a href={href} className="text-primary font-medium hover:underline" {...rest}>
			{children}
		</a>
	);
}

export default AuthCard;
